import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Button } from '../Buttons/Button';
import { PaymentItem } from 'components/Modules/DataDisplay/PhysicianSearchGrid';
import { usePaymentPageToken } from 'api/usePaymentPageToken';
import { TextBox } from '../Typography/TextBox';
import { PaymentConfirmationDisclosure } from './PaymentConfirmationDisclosure';

export interface ConfirmPaymentDialogProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (token: string) => void;
  items: PaymentItem[];
}

function formatToUSD(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
}

export const ConfirmPaymentDialog = ({
  open,
  handleClose,
  handleConfirm,
  items,
}: ConfirmPaymentDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { data } = usePaymentPageToken({
    items: items.map(x => ({
      name: x.firstName + ' ' + x.lastName,
      amount: +x.amount as unknown as number,
      licenseNumber: x.license,
    })),
  });
  const otherdata = data as unknown as { token: string };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogTitle id="alert-dialog-title">
        {'Please confirm your selected provider information'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
          Please verify that the following provider information is correct before proceeding with
          your payment. If the information is incorrect, please return to the previous screen and
          select the correct individual from the Search Physician or License dropdown.
        </DialogContentText>
        <TextBox sx={{ mb: 2 }}>
          <Typography fontWeight="bold" mb={1} sx={{ textDecoration: 'underline' }}>
            PLEASE READ - IMPORTANT NOTICES:
          </Typography>
          <Typography fontWeight="bold" mb={1}>
            - <span style={{ textDecoration: 'underline' }}>DO NOT PAY</span> if you are exempt. If
            you are unsure if you qualify for an exemption, please visit:{' '}
            <Link
              href="https://nica.com/medical-providers/exemptions/#documents-required"
              target="_BLANK"
              color="#00548B"
            >
              Exemptions
            </Link>
            .
          </Typography>
          <Typography fontWeight="bold" mb={1}>
            - There may be other physicians in our system that share your name. Please verify you
            have entered the correct license number AND name before you proceed.
          </Typography>
        </TextBox>
        <Box sx={{ height: 300, width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              padding: 1,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              backgroundColor: 'background.default',
            }}
          >
            <Typography flex={1}>License Number</Typography>
            <Typography flex={1}>Name</Typography>
            <Typography flex={1} textAlign="right">
              Amount
            </Typography>
          </Stack>
          {items.map((item, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              sx={{
                padding: 1,
                borderBottom: '1px solid',
                borderColor: 'primary.light',
              }}
            >
              <Typography fontSize={14} flex={1}>
                {item.license}
              </Typography>
              <Typography fontSize={14} flex={1}>
                {item.firstName} {item.lastName}
              </Typography>
              <Typography fontSize={14} flex={1} textAlign="right">
                {formatToUSD(item.amount)}
              </Typography>
            </Stack>
          ))}
          <TextBox sx={{ mt: 2 }}>
            <Typography align="right" variant="h5" component="p">
              Total Amount: {formatToUSD(items.reduce((a, b) => a + Number(b.amount), 0))}
            </Typography>
          </TextBox>
          <PaymentConfirmationDisclosure />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="medium" variant="outlined" color="warning" onClick={handleClose}>
          Go Back
        </Button>
        <Button
          size="medium"
          variant="contained"
          color="primary"
          onClick={() => handleConfirm(otherdata?.token ?? '')}
          disabled={!otherdata?.token}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
